import {
  Box,
  Button,
  Card,
  Heading,
  HStack,
  Image,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  AlertCircle,
  ArrowRight,
  Bookmark,
  Check,
  MessageCircle,
  RefreshCw,
  Server,
  Shield,
} from 'lucide-react'
import mqttLWT from '@/assets/book-black.png'
import mqttHandShake from '@/assets/handshake-black.svg'
import mqttOverview from '@/assets/mqtt-overview.webp'
import mqttRetain from '@/assets/retained-mag-black.png'
import mqttClient from '@/assets/robot.svg'
import mqttServer from '@/assets/server-black.svg'
import mqttQOS from '@/assets/simple-to-deploy.svg'
import { PageHeader, SectionHeading } from '@/components/shell'

export const Route = createFileRoute('/_app/education/mqtt')({
  component: MQTTEducationPage,
})

interface ConceptCardProps {
  image: string
  title: string
  description: string
  imageSize?: string
}

function ConceptCard({ image, title, description, imageSize = '80px' }: ConceptCardProps) {
  return (
    <Card.Root transition="all 0.3s" _hover={{ shadow: 'md' }}>
      <Card.Body p={6}>
        <VStack align="center" gap={4} textAlign="center">
          <Image src={image} alt={title} h={imageSize} objectFit="contain" />
          <VStack gap={2}>
            <Text fontWeight="bold" fontSize="lg">
              {title}
            </Text>
            <Text fontSize="sm" color="content.secondary">
              {description}
            </Text>
          </VStack>
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}

interface FeatureItemProps {
  title: string
  description: string
}

function _FeatureItem({ title, description }: FeatureItemProps) {
  return (
    <HStack gap={3} align="flex-start">
      <Box p={1} bg="green.100" borderRadius="full" flexShrink={0} mt={1}>
        <Check size={14} color="var(--chakra-colors-green-600)" />
      </Box>
      <VStack align="start" gap={0}>
        <Text fontWeight="bold">{title}</Text>
        <Text fontSize="sm" color="content.secondary">
          {description}
        </Text>
      </VStack>
    </HStack>
  )
}

interface FeatureCardProps {
  icon: typeof Shield
  title: string
  description: string
}

function FeatureCard({ icon: Icon, title, description }: FeatureCardProps) {
  return (
    <Card.Root transition="all 0.3s" _hover={{ shadow: 'md' }}>
      <Card.Body p={5} mt="4" mb="4">
        <VStack align="start" gap={3}>
          <Box p={2} bg="brand.solid" borderRadius="lg">
            <Icon size={20} />
          </Box>
          <VStack align="start" gap={1}>
            <Text fontWeight="bold">{title}</Text>
            <Text fontSize="sm" color="content.secondary">
              {description}
            </Text>
          </VStack>
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}

const tags = ['IoT Protocol', 'Pub/Sub', 'Low Bandwidth', 'OASIS Standard']

function MQTTEducationPage() {
  const navigate = useNavigate()

  const coreFeatures = [
    {
      icon: Server,
      title: 'Lightweight Protocol',
      description:
        'Minimal packet overhead makes MQTT perfect for constrained devices and low-bandwidth networks.',
    },
    {
      icon: MessageCircle,
      title: 'Publish-Subscribe',
      description:
        'Decouples message producers from consumers, enabling flexible and scalable architectures.',
    },
    {
      icon: Shield,
      title: 'Quality of Service',
      description:
        'Three QoS levels ensure message delivery reliability based on your requirements.',
    },
    {
      icon: RefreshCw,
      title: 'Persistent Sessions',
      description:
        'Sessions survive disconnections, ensuring reliable communication over unreliable networks.',
    },
    {
      icon: Bookmark,
      title: 'Retained Messages',
      description: 'New subscribers immediately receive the last known value on subscription.',
    },
    {
      icon: AlertCircle,
      title: 'Last Will & Testament',
      description: 'Automatic notification when clients disconnect ungracefully.',
    },
  ]

  const concepts = [
    {
      image: mqttClient,
      title: 'MQTT Clients',
      description:
        'MQTT clients publish messages to an MQTT broker and subscribe to messages they want to receive. Implementations require minimal footprint, making them ideal for deployment on small constrained devices with efficient bandwidth usage.',
    },
    {
      image: mqttServer,
      title: 'MQTT Broker',
      description:
        'MQTT brokers receive published messages and dispatch them to subscribing MQTT clients. Messages contain topics that clients subscribe to, and brokers use subscription lists to determine message recipients.',
      imageSize: '60px',
    },
    {
      image: mqttQOS,
      title: 'Quality of Service',
      description:
        'MQTT implements 3 QoS levels: At most once (0), At least once (1), and Exactly once (2). These levels allow reliable IoT applications to adapt to unreliable network conditions.',
    },
  ]

  const advancedConcepts = [
    {
      image: mqttHandShake,
      title: 'Persistent Sessions',
      description:
        'MQTT allows persistent sessions between client and broker. Sessions persist even during network disconnections. Once reconnected, session information enables seamless client reconnection - a key feature making MQTT more efficient than HTTP for unreliable cellular networks.',
      imageSize: '60px',
    },
    {
      image: mqttRetain,
      title: 'Retained Messages',
      description:
        'New MQTT subscribers have no insight into when to expect their first message. However, brokers can store retained messages sent immediately upon new subscriptions, ensuring clients receive at least one message right away.',
      imageSize: '60px',
    },
    {
      image: mqttLWT,
      title: 'Last Will & Testament',
      description:
        'MQTT clients can specify a "last will" message to the broker, which will be sent if the client disconnects ungracefully. This enables graceful system-wide notifications when clients become unavailable.',
      imageSize: '60px',
    },
  ]

  return (
    <Box maxW="1400px" mx="auto">
      <PageHeader
        title="Introduction to MQTT"
        subtitle="The lightweight messaging protocol that powers the Internet of Things"
      />

      <VStack align="stretch" gap={10}>
        {/* Tags */}
        <HStack gap={2} flexWrap="wrap" justify="center">
          {tags.map((tag) => (
            <Box key={tag} px={3} py={1} bg="brand.solid" borderRadius="full">
              <Text fontSize="sm" fontWeight="medium" color="gray.900">
                {tag}
              </Text>
            </Box>
          ))}
        </HStack>

        {/* Overview Image */}
        <Card.Root overflow="hidden">
          <Card.Body p={0}>
            <Image src={mqttOverview} alt="MQTT Overview" w="100%" objectFit="cover" />
          </Card.Body>
        </Card.Root>

        {/* History Section */}
        <VStack align="stretch" gap={4}>
          <SectionHeading title="The Origins of MQTT" />
          <Card.Root>
            <Card.Body p={6} mt="4" mb="4">
              <VStack align="start" gap={4}>
                <Text fontSize="md">
                  <strong>MQTT (Message Queuing Telemetry Transport)</strong> was developed in{' '}
                  <strong>1999 by IBM's Andy Stanford-Clark and Arlen Nipper of Eurotech</strong>,
                  primarily to address the need for a lightweight, low-bandwidth protocol to connect
                  remote devices with minimal power and network requirements.
                </Text>
                <Text fontSize="md">
                  Originally designed for monitoring oil pipelines via satellite connections,{' '}
                  <strong>MQTT was ideal for scenarios where network bandwidth was limited</strong>,
                  latency needed to be low, and devices had restricted processing power.
                </Text>
                <Text fontSize="md">
                  Its <strong>publish-subscribe</strong> messaging model, which decouples message
                  producers (publishers) from consumers (subscribers), allowed for{' '}
                  <strong>efficient and scalable communication</strong> among devices.
                </Text>
                <Text fontSize="md">
                  MQTT has since become widely adopted in the{' '}
                  <strong>Internet of Things (IoT) field</strong>, as its design suits many IoT
                  applications where devices require reliable, real-time communication over
                  potentially unreliable networks.
                </Text>
                <Text fontSize="md">
                  In 2013, <strong>MQTT was standardized under the OASIS consortium</strong>,
                  further driving its adoption in industry and modern IoT ecosystems.
                </Text>
              </VStack>
            </Card.Body>
          </Card.Root>
        </VStack>

        {/* Core Features Section */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            title="Core Features"
            description="MQTT provides essential features for reliable IoT communication"
          />
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={4}>
            {coreFeatures.map((feature) => (
              <FeatureCard key={feature.title} {...feature} />
            ))}
          </SimpleGrid>
        </VStack>

        {/* Core Concepts Section */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            title="Understanding MQTT"
            description="The fundamental building blocks of MQTT communication"
          />
          <SimpleGrid columns={{ base: 1, md: 3 }} gap={6}>
            {concepts.map((concept) => (
              <ConceptCard key={concept.title} {...concept} />
            ))}
          </SimpleGrid>
        </VStack>

        {/* Advanced Concepts Section */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            title="MQTT Features for Reliability"
            description="Features that make MQTT ideal for unreliable networks"
          />
          <SimpleGrid columns={{ base: 1, md: 3 }} gap={6}>
            {advancedConcepts.map((concept) => (
              <ConceptCard key={concept.title} {...concept} imageSize={concept.imageSize} />
            ))}
          </SimpleGrid>
        </VStack>

        {/* QoS Deep Dive */}
        <VStack align="stretch" gap={4}>
          <SectionHeading title="Message Delivery Guarantees" />
          <SimpleGrid columns={{ base: 1, lg: 3 }} gap={4}>
            <Card.Root>
              <Card.Body p={6} mt="4" mb="4">
                <VStack align="start" gap={3}>
                  <HStack>
                    <Box px={3} py={1} bg="blue.500" borderRadius="full">
                      <Text fontSize="sm" fontWeight="bold" color="white">
                        QoS 0
                      </Text>
                    </Box>
                  </HStack>
                  <Text fontWeight="bold" fontSize="lg">
                    At Most Once
                  </Text>
                  <Text fontSize="sm" color="content.secondary">
                    Fire and forget - the message is sent once with no acknowledgment. Best for
                    non-critical data where occasional loss is acceptable.
                  </Text>
                </VStack>
              </Card.Body>
            </Card.Root>

            <Card.Root>
              <Card.Body p={6} mt="4" mb="4">
                <VStack align="start" gap={3}>
                  <HStack>
                    <Box px={3} py={1} bg="green.500" borderRadius="full">
                      <Text fontSize="sm" fontWeight="bold" color="white">
                        QoS 1
                      </Text>
                    </Box>
                  </HStack>
                  <Text fontWeight="bold" fontSize="lg">
                    At Least Once
                  </Text>
                  <Text fontSize="sm" color="content.secondary">
                    Message is guaranteed to arrive, but duplicates are possible. Suitable for most
                    IoT use cases where delivery is critical.
                  </Text>
                </VStack>
              </Card.Body>
            </Card.Root>

            <Card.Root>
              <Card.Body p={6} mt="4" mb="4">
                <VStack align="start" gap={3}>
                  <HStack>
                    <Box px={3} py={1} bg="purple.500" borderRadius="full">
                      <Text fontSize="sm" fontWeight="bold" color="white">
                        QoS 2
                      </Text>
                    </Box>
                  </HStack>
                  <Text fontWeight="bold" fontSize="lg">
                    Exactly Once
                  </Text>
                  <Text fontSize="sm" color="content.secondary">
                    Guaranteed single delivery using 4-way handshake. Essential for billing,
                    financial, or critical command messages.
                  </Text>
                </VStack>
              </Card.Body>
            </Card.Root>
          </SimpleGrid>
        </VStack>

        {/* CTA */}
        <Card.Root bg="bg.subtle">
          <Card.Body p={8}>
            <HStack justify="space-between" align="center" flexWrap="wrap" gap={4}>
              <VStack align="start" gap={1}>
                <Heading size="lg">Continue Learning</Heading>
                <Text color="content.secondary">
                  Discover Sparkplug B, the industrial IoT specification built on MQTT
                </Text>
              </VStack>
              <Button size="lg" onClick={() => navigate({ to: '/education/sparkplug' })}>
                Learn Sparkplug
                <ArrowRight size={18} />
              </Button>
            </HStack>
          </Card.Body>
        </Card.Root>
      </VStack>
    </Box>
  )
}
